/**
 * Production CSS Manager for Shadow DOM
 * 
 * Handles CSS extraction and injection for Tailwind + shadcn/ui components
 * in isolated Shadow DOM environments for the main SDK.
 */

import { shadowLogger } from '../utils/logger'

// Import compiled CSS - will be handled by Vite plugin during build
let compiledCSS: string = ''

// This will be populated by the Vite plugin during build
declare global {
  var __KWRY_SHADOW_CSS__: string | undefined
}

// Load CSS from global variable set by Vite plugin
if (typeof globalThis !== 'undefined' && globalThis.__KWRY_SHADOW_CSS__) {
  compiledCSS = globalThis.__KWRY_SHADOW_CSS__
  shadowLogger.debug('✅ Compiled CSS loaded from global variable', {
    length: compiledCSS.length,
    preview: compiledCSS.substring(0, 100)
  })
} else {
  shadowLogger.error('❌ No compiled CSS found! Vite plugin failed to extract CSS.')
}

/**
 * Production CSS Manager for Shadow DOM
 * Handles CSS injection with proper error handling and fallbacks
 */
export class ShadowCSSManager {
  private static injectedRoots = new WeakSet<ShadowRoot>()

  /**
   * Generate Shadow DOM theme CSS to fix theme variable selectors
   * Extracts theme variables from :root and .dark and creates equivalent rules for #kwry-shadow-root
   */
  private static generateShadowThemeCSS(): string {
    try {
      // Extract theme variables from :root selector
      const rootMatch = compiledCSS.match(/:root\s*\{([^}]+)\}/)
      const darkMatch = compiledCSS.match(/\.dark\s*\{([^}]+)\}/)
      
      let shadowThemeCSS = ''
      
      if (rootMatch) {
        // Create light theme rule for our shadow container
        shadowThemeCSS += `#kwry-shadow-root[data-theme="light"], #kwry-shadow-root:not(.dark):not([data-theme="dark"]) { ${rootMatch[1]} }\n`
      }
      
      if (darkMatch) {
        // Create dark theme rules for our shadow container  
        shadowThemeCSS += `#kwry-shadow-root[data-theme="dark"], #kwry-shadow-root.dark { ${darkMatch[1]} }\n`
      }
      
      if (shadowThemeCSS) {
        shadowLogger.debug('✅ Generated Shadow DOM theme CSS', {
          hasLight: !!rootMatch,
          hasDark: !!darkMatch,
          cssLength: shadowThemeCSS.length
        })
      }
      
      return shadowThemeCSS
      
    } catch (error) {
      shadowLogger.error('Failed to generate Shadow DOM theme CSS:', error)
      return ''
    }
  }

  /**
   * Inject CSS styles into Shadow DOM
   * @param shadowRoot - The Shadow DOM root to inject CSS into
   * @param customCSS - Optional custom CSS to append
   */
  static async injectStyles(
    shadowRoot: ShadowRoot, 
    customCSS?: string
  ): Promise<void> {
    try {
      shadowLogger.debug('Injecting CSS styles into Shadow DOM...')
      
      // Prevent double injection
      if (this.injectedRoots.has(shadowRoot)) {
        shadowLogger.debug('CSS already injected for this Shadow DOM')
        return
      }

      // Validate CSS content
      if (!compiledCSS) {
        throw new Error('No compiled CSS available for injection')
      }

      // Generate Shadow DOM theme CSS fix
      const shadowThemeCSS = this.generateShadowThemeCSS()
      const combinedCustomCSS = shadowThemeCSS + (customCSS || '')

      // Try modern approach first, fallback to legacy
      if (this.supportsAdoptedStyleSheets(shadowRoot)) {
        await this.injectWithAdoptedStyleSheets(shadowRoot, combinedCustomCSS)
      } else {
        this.injectWithStyleTag(shadowRoot, combinedCustomCSS)
      }

      // Mark as injected
      this.injectedRoots.add(shadowRoot)
      
      shadowLogger.info('✅ CSS styles injected successfully')
      
    } catch (error) {
      shadowLogger.error('❌ Failed to inject CSS styles:', error)
      
      // Emergency fallback
      try {
        const shadowThemeCSS = this.generateShadowThemeCSS()
        const combinedCustomCSS = shadowThemeCSS + (customCSS || '')
        this.injectWithStyleTag(shadowRoot, combinedCustomCSS)
        this.injectedRoots.add(shadowRoot)
        shadowLogger.info('✅ CSS injected via fallback method')
      } catch (fallbackError) {
        shadowLogger.error('❌ Fallback CSS injection also failed:', fallbackError)
        throw fallbackError
      }
    }
  }

  /**
   * Check if browser supports adoptedStyleSheets
   */
  private static supportsAdoptedStyleSheets(shadowRoot: ShadowRoot): boolean {
    return (
      'adoptedStyleSheets' in shadowRoot && 
      typeof CSSStyleSheet !== 'undefined' &&
      typeof CSSStyleSheet.prototype.replaceSync === 'function'
    )
  }

  /**
   * Inject CSS using adoptedStyleSheets (modern browsers)
   */
  private static async injectWithAdoptedStyleSheets(
    shadowRoot: ShadowRoot, 
    customCSS?: string
  ): Promise<void> {
    const sheets: CSSStyleSheet[] = []

    // Main Tailwind/shadcn CSS
    const mainSheet = new CSSStyleSheet()
    mainSheet.replaceSync(compiledCSS)
    sheets.push(mainSheet)

    // Custom CSS if provided
    if (customCSS) {
      const customSheet = new CSSStyleSheet()
      customSheet.replaceSync(customCSS)
      sheets.push(customSheet)
    }

    shadowRoot.adoptedStyleSheets = sheets
    
    shadowLogger.debug('✅ CSS injected via adoptedStyleSheets', {
      sheets: sheets.length,
      mainSize: compiledCSS.length,
      customSize: customCSS?.length || 0
    })
  }

  /**
   * Inject CSS using style tags (fallback for older browsers)
   */
  private static injectWithStyleTag(
    shadowRoot: ShadowRoot, 
    customCSS?: string
  ): void {
    // Main Tailwind/shadcn CSS
    const mainStyle = document.createElement('style')
    mainStyle.setAttribute('data-kwry-css', 'main')
    mainStyle.textContent = compiledCSS
    shadowRoot.appendChild(mainStyle)

    // Custom CSS if provided
    if (customCSS) {
      const customStyle = document.createElement('style')
      customStyle.setAttribute('data-kwry-css', 'custom')
      customStyle.textContent = customCSS
      shadowRoot.appendChild(customStyle)
    }
    
    shadowLogger.debug('✅ CSS injected via style tags', {
      mainSize: compiledCSS.length,
      customSize: customCSS?.length || 0
    })
  }

  /**
   * Update custom CSS in an already-injected Shadow DOM
   */
  static updateCustomCSS(shadowRoot: ShadowRoot, newCustomCSS: string): void {
    try {
      if (!this.injectedRoots.has(shadowRoot)) {
        shadowLogger.warn('Attempting to update CSS in non-injected Shadow DOM')
        return
      }

      if (this.supportsAdoptedStyleSheets(shadowRoot)) {
        // Update adoptedStyleSheets
        const sheets = [...(shadowRoot.adoptedStyleSheets || [])]
        
        // Remove existing custom sheet if present
        const customSheetIndex = sheets.findIndex(sheet => 
          sheet.cssRules.length > 0 && 
          Array.from(sheet.cssRules).some(rule => 
            rule.cssText.includes('/* custom */')
          )
        )
        
        if (customSheetIndex >= 0) {
          sheets.splice(customSheetIndex, 1)
        }

        // Add new custom sheet
        if (newCustomCSS) {
          const customSheet = new CSSStyleSheet()
          customSheet.replaceSync(`/* custom */ ${newCustomCSS}`)
          sheets.push(customSheet)
        }

        shadowRoot.adoptedStyleSheets = sheets
      } else {
        // Update style tags
        const existingCustomStyle = shadowRoot.querySelector('style[data-kwry-css="custom"]')
        if (existingCustomStyle) {
          existingCustomStyle.textContent = newCustomCSS
        } else if (newCustomCSS) {
          const customStyle = document.createElement('style')
          customStyle.setAttribute('data-kwry-css', 'custom')
          customStyle.textContent = newCustomCSS
          shadowRoot.appendChild(customStyle)
        }
      }

      shadowLogger.debug('✅ Custom CSS updated successfully')
      
    } catch (error) {
      shadowLogger.error('❌ Failed to update custom CSS:', error)
    }
  }

  /**
   * Test CSS isolation by checking if styles leak outside Shadow DOM
   */
  static testIsolation(shadowRoot: ShadowRoot): boolean {
    try {
      // Create test element inside Shadow DOM
      const shadowTestDiv = document.createElement('div')
      shadowTestDiv.className = 'kwry-test-isolation bg-red-500 p-4'
      shadowTestDiv.style.cssText = 'position: absolute; top: -9999px; left: -9999px;'
      shadowRoot.appendChild(shadowTestDiv)

      // Create test element in host document
      const hostTestDiv = document.createElement('div')
      hostTestDiv.className = 'kwry-test-isolation bg-red-500 p-4'
      hostTestDiv.style.cssText = 'position: absolute; top: -9999px; left: -9999px;'
      document.body.appendChild(hostTestDiv)

      // Check computed styles
      const shadowStyles = window.getComputedStyle(shadowTestDiv)
      const hostStyles = window.getComputedStyle(hostTestDiv)

      // Shadow DOM element should have Tailwind styles applied
      const shadowHasStyles = shadowStyles.backgroundColor !== 'rgba(0, 0, 0, 0)'
      
      // Host element should NOT have Tailwind styles (isolation working)
      const hostHasStyles = hostStyles.backgroundColor !== 'rgba(0, 0, 0, 0)'

      // Clean up
      shadowRoot.removeChild(shadowTestDiv)
      document.body.removeChild(hostTestDiv)

      const isolated = shadowHasStyles && !hostHasStyles

      shadowLogger.debug('CSS isolation test result:', {
        isolated,
        shadowHasStyles,
        hostHasStyles,
        shadowBg: shadowStyles.backgroundColor,
        hostBg: hostStyles.backgroundColor
      })

      return isolated

    } catch (error) {
      shadowLogger.error('CSS isolation test failed:', error)
      return false
    }
  }

  /**
   * Get information about the loaded CSS
   */
  static getCSSInfo() {
    return {
      hasCSS: !!compiledCSS,
      size: compiledCSS?.length || 0,
      preview: compiledCSS?.substring(0, 200) || '',
      hasTailwind: compiledCSS?.includes('--tw-') || false,
      hasCustomProps: compiledCSS?.includes('--primary') || false
    }
  }

  /**
   * Remove CSS from Shadow DOM (for cleanup)
   */
  static removeStyles(shadowRoot: ShadowRoot): void {
    try {
      if (this.supportsAdoptedStyleSheets(shadowRoot)) {
        shadowRoot.adoptedStyleSheets = []
      } else {
        const styleElements = shadowRoot.querySelectorAll('style[data-kwry-css]')
        styleElements.forEach(style => style.remove())
      }

      this.injectedRoots.delete(shadowRoot)
      shadowLogger.debug('✅ CSS styles removed from Shadow DOM')
      
    } catch (error) {
      shadowLogger.error('❌ Failed to remove CSS styles:', error)
    }
  }
}